import { useMemo } from 'react';
import { useAppTheme } from '../../theme/PreferencesContext';

// Categorical palette, ported from mobileapp/src/components/charts/chartTheme.ts,
// including its dark half now that the webapp has a dark mode.
//
// The ORDER (blue / amber / violet / emerald) is deliberate: blue↔violet are
// too close under deuteranopia to sit next to each other, so amber goes
// between them.
//
// The DARK STEPS differ from the light ones for the same reason they do on
// mobile: chart fills sit on the card colour (#111827 here too), whose readable
// lightness band is narrower and darker than light mode's. Re-validate before
// changing any value — the pairs are not independent, and reordering alone is
// enough to break them.
const CATEGORICAL = {
  light: ['#2563EB', '#D97706', '#7C3AED', '#059669'],
  dark: ['#3B82F6', '#D97706', '#8B5CF6', '#059669'],
};

// Anything past the fourth slot folds into one neutral rather than getting a
// generated fifth hue, which would be indistinguishable under colour blindness.
const OTHER = { light: '#94A3B8', dark: '#64748B' };

// Reserved status colours, never reused as "series 5", and always shipped with
// a written label — never colour alone. These match --success / --warning /
// --danger in styles/style.css, which are the same in both themes.
const STATUS = { good: '#10b981', warning: '#f59e0b', critical: '#ef4444' };

/** How many categorical slots exist before the tail folds into "Other". */
export const SERIES_SLOTS = CATEGORICAL.light.length;

export interface ChartColors {
  /** Fixed-order categorical hues. Never cycled — see `series` below. */
  categorical: string[];
  /**
   * Hue for series `index`, by position. Past the fourth this returns the
   * neutral rather than wrapping back to blue, so two different series can
   * never end up sharing a colour inside one chart.
   */
  series: (index: number) => string;
  other: string;
  /**
   * Single hue for magnitude comparisons (ranked bars). One hue, not four — a
   * ranked bar list is one series, and colouring each row differently implies
   * a category difference that isn't there.
   */
  sequential: string;
  status: typeof STATUS;
}

/**
 * A hook rather than a constant because the palette depends on the active
 * theme (this mirrors mobileapp's useChartColors). Memoised on the theme, so
 * the returned object is stable across re-renders and safe to list in a
 * useMemo dependency array.
 */
export const useChartColors = (): ChartColors => {
  const { isDarkMode } = useAppTheme();

  return useMemo(() => {
    const mode = isDarkMode ? 'dark' : 'light';
    const categorical = CATEGORICAL[mode];
    return {
      categorical,
      series: (index: number) => categorical[index] ?? OTHER[mode],
      other: OTHER[mode],
      sequential: categorical[0],
      status: STATUS,
    };
  }, [isDarkMode]);
};
